import React, { useState } from 'react';
import { 
  XMarkIcon, 
  PhoneIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { Call } from '../types/Call';
import { getCallStatusColor, getCallStatusLabel, getCallTypeColor, getCallTypeLabel, formatCallDate, formatCallDuration } from '../services/callApi';

interface CallDetailModalProps {
  call: Call;
  onClose: () => void;
  onReschedule: (callId: number, newDate: string, reason: string) => void;
  onCancel: (callId: number, reason: string) => void;
}

const CallDetailModal: React.FC<CallDetailModalProps> = ({ call, onClose, onReschedule, onCancel }) => {
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const handleReschedule = () => {
    if (!rescheduleDate || !rescheduleTime || !rescheduleReason.trim()) {
      return;
    }

    const newDateTime = new Date(`${rescheduleDate}T${rescheduleTime}`);
    onReschedule(call.id, newDateTime.toISOString(), rescheduleReason);
    setShowRescheduleModal(false);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
  };

  const handleCancel = () => {
    if (!cancelReason.trim()) {
      return;
    }

    onCancel(call.id, cancelReason);
    setShowCancelModal(false);
    setCancelReason('');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <ClockIcon className="w-5 h-5" />;
      case 'in_progress':
        return <PhoneIcon className="w-5 h-5" />;
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'cancelled':
        return <XCircleIcon className="w-5 h-5" />;
      case 'rescheduled':
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      default:
        return <ClockIcon className="w-5 h-5" />;
    }
  };

  const canReschedule = call.status === 'scheduled' || call.status === 'in_progress';
  const canCancel = call.status === 'scheduled' || call.status === 'in_progress';

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
          <div className="mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Call Details</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Call Information */}
              <div className="space-y-6">
                {/* Status and Type */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${getCallStatusColor(call.status)}`}>
                      {getStatusIcon(call.status)}
                      <span className="ml-2">{getCallStatusLabel(call.status)}</span>
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getCallTypeColor(call.callType)}`}>
                      {getCallTypeLabel(call.callType)}
                    </span>
                  </div>
                </div>

                {/* Lead Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Lead Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <UserIcon className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-sm text-gray-900">
                        {call.leadFirstName} {call.leadLastName}
                      </span>
                    </div>
                    {call.leadCompany && (
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="w-4 h-4 text-gray-400 mr-3" />
                        <span className="text-sm text-gray-900">{call.leadCompany}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <PhoneIcon className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-sm text-gray-900">{call.phoneNumber}</span>
                    </div>
                    <div className="text-sm text-gray-500">{call.leadEmail}</div>
                  </div>
                </div>

                {/* Call Details */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Motive/Agenda</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {call.motiveAgenda}
                    </p>
                  </div>

                  {call.summary && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Summary</label>
                      <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                        {call.summary}
                      </p>
                    </div>
                  )}

                  {call.rescheduleReason && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Reschedule/Cancel Reason</label>
                      <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                        {call.rescheduleReason}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Timing and Actions */}
              <div className="space-y-6">
                {/* Timing Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Timing Information</h4>
                  <div className="space-y-3">
                    {call.scheduledDate && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</label>
                        <p className="text-sm text-gray-900">{formatCallDate(call.scheduledDate)}</p>
                      </div>
                    )}
                    
                    {call.actualStartTime && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Started</label>
                        <p className="text-sm text-gray-900">{formatCallDate(call.actualStartTime)}</p>
                      </div>
                    )}
                    
                    {call.actualEndTime && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ended</label>
                        <p className="text-sm text-gray-900">{formatCallDate(call.actualEndTime)}</p>
                      </div>
                    )}
                    
                    {call.durationFormatted && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</label>
                        <p className="text-sm text-gray-900">{call.durationFormatted}</p>
                      </div>
                    )}

                    {call.rescheduledToDate && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Rescheduled To</label>
                        <p className="text-sm text-gray-900">{formatCallDate(call.rescheduledToDate)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Creator Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Created By</h4>
                  <p className="text-sm text-gray-900">
                    {call.createdByFirstName} {call.createdByLastName}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatCallDate(call.createdAt)}
                  </p>
                </div>

                {/* Actions */}
                {(canReschedule || canCancel) && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">Actions</h4>
                    
                    {canReschedule && (
                      <button
                        onClick={() => setShowRescheduleModal(true)}
                        className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded-md hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Reschedule Call
                      </button>
                    )}
                    
                    {canCancel && (
                      <button
                        onClick={() => setShowCancelModal(true)}
                        className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <XCircleIcon className="w-4 h-4 mr-2" />
                        Cancel Call
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full" style={{ zIndex: 60 }}>
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Reschedule Call</h4>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Date</label>
                    <input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Time</label>
                    <input
                      type="time"
                      value={rescheduleTime}
                      onChange={(e) => setRescheduleTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <textarea
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                    rows={3}
                    placeholder="Why are you rescheduling this call?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowRescheduleModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReschedule}
                  disabled={!rescheduleDate || !rescheduleTime || !rescheduleReason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reschedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full" style={{ zIndex: 60 }}>
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Cancel Call</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Cancellation</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={4}
                  placeholder="Why are you cancelling this call?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCancel}
                  disabled={!cancelReason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel Call
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CallDetailModal;
